import React from 'react';
import { 
  Building2, 
  Heart, 
  Truck, 
  ShoppingCart, 
  Code, 
  Megaphone, 
  Users,
  ArrowRight 
} from 'lucide-react';

const Industries = () => {
  const industries = [
    {
      icon: <Building2 className="w-10 h-10 text-blue-600" />,
      title: "Finance and Banking",
      description: "Secure, compliant, and scalable financial technology solutions",
      features: ["Payment Processing", "Risk Management", "Compliance Solutions", "Digital Banking"],
      color: "bg-blue-50 hover:bg-blue-100"
    },
    {
      icon: <Heart className="w-10 h-10 text-red-600" />,
      title: "Healthcare",
      description: "HIPAA-compliant healthcare technology and data management",
      features: ["Patient Management", "Electronic Health Records", "Telemedicine", "Healthcare Analytics"],
      color: "bg-red-50 hover:bg-red-100"
    },
    {
      icon: <Truck className="w-10 h-10 text-green-600" />,
      title: "Supply Chain",
      description: "Optimize logistics and supply chain operations with smart solutions",
      features: ["Inventory Management", "Logistics Optimization", "Track & Trace", "Demand Forecasting"],
      color: "bg-green-50 hover:bg-green-100"
    },
    {
      icon: <ShoppingCart className="w-10 h-10 text-purple-600" />,
      title: "Retail and E-commerce",
      description: "Drive sales and enhance customer experience with modern retail tech",
      features: ["E-commerce Platforms", "Customer Analytics", "Inventory Systems", "POS Solutions"],
      color: "bg-purple-50 hover:bg-purple-100"
    },
    {
      icon: <Code className="w-10 h-10 text-orange-600" />,
      title: "Open Source",
      description: "Contributing to and building upon open source technologies",
      features: ["Community Projects", "OSS Contributions", "Custom Solutions", "Documentation"],
      color: "bg-orange-50 hover:bg-orange-100"
    },
    {
      icon: <Megaphone className="w-10 h-10 text-pink-600" />,
      title: "Media & Marketing",
      description: "Digital marketing platforms and media management solutions",
      features: ["Content Management", "Campaign Analytics", "Social Media Tools", "Marketing Automation"],
      color: "bg-pink-50 hover:bg-pink-100"
    },
    {
      icon: <Users className="w-10 h-10 text-indigo-600" />,
      title: "Non-Profit Organisation",
      description: "Technology solutions to help non-profits achieve their missions",
      features: ["Donor Management", "Volunteer Platforms", "Impact Tracking", "Fundraising Tools"],
      color: "bg-indigo-50 hover:bg-indigo-100"
    }
  ];

  const scrollToContact = () => {
    const element = document.querySelector('#contact');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section id="industries" className="section-padding bg-secondary-50">
      <div className="container-custom">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-secondary-900 mb-4">
            Industries We Serve
          </h2>
          <p className="text-xl text-secondary-600 max-w-3xl mx-auto">
            We bring specialized expertise to diverse industries, understanding unique 
            challenges and delivering tailored solutions that drive success.
          </p>
        </div>

        {/* Industries Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
          {industries.map((industry, index) => (
            <div 
              key={index}
              className={`${industry.color} rounded-2xl p-8 transition-all duration-300 border border-transparent hover:border-primary-200 hover:shadow-lg`}
            >
              {/* Icon */}
              <div className="mb-6">
                {industry.icon}
              </div>

              {/* Content */}
              <h3 className="text-2xl font-bold text-secondary-900 mb-4">
                {industry.title}
              </h3>
              <p className="text-secondary-700 mb-6 leading-relaxed">
                {industry.description}
              </p>

              {/* Features */}
              <div className="space-y-2 mb-6">
                {industry.features.map((feature, featureIndex) => (
                  <div key={featureIndex} className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-primary-500 rounded-full"></div>
                    <span className="text-sm text-secondary-600">{feature}</span>
                  </div>
                ))}
              </div>

              {/* CTA */}
              <button 
                onClick={scrollToContact}
                className="text-primary-600 hover:text-primary-700 font-medium flex items-center space-x-2 group"
              >
                <span>Discuss Your Project</span>
                <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform duration-200" />
              </button>
            </div>
          ))}
        </div>

        {/* Industry Stats */}
        <div className="bg-white rounded-2xl p-8 md:p-12 shadow-lg">
          <div className="text-center mb-12">
            <h3 className="text-2xl md:text-3xl font-bold text-secondary-900 mb-4">
              Our Industry Impact
            </h3>
            <p className="text-secondary-600 max-w-2xl mx-auto">
              Delivering measurable results across various sectors with proven expertise and innovation.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <div>
              <div className="text-3xl md:text-4xl font-bold text-primary-600 mb-2">7+</div>
              <div className="text-secondary-600">Industries Served</div>
            </div>
            <div>
              <div className="text-3xl md:text-4xl font-bold text-primary-600 mb-2">50+</div>
              <div className="text-secondary-600">Industry Solutions</div>
            </div>
            <div>
              <div className="text-3xl md:text-4xl font-bold text-primary-600 mb-2">98%</div>
              <div className="text-secondary-600">Client Satisfaction</div>
            </div>
            <div>
              <div className="text-3xl md:text-4xl font-bold text-primary-600 mb-2">24/7</div>
              <div className="text-secondary-600">Support Available</div>
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="mt-16 text-center">
          <div className="bg-gradient-to-r from-primary-600 to-primary-700 rounded-2xl p-8 md:p-12 text-white">
            <h3 className="text-2xl md:text-3xl font-bold mb-4">
              Don't See Your Industry?
            </h3>
            <p className="text-primary-100 mb-8 max-w-2xl mx-auto">
              We work with businesses across all sectors. Our adaptable approach 
              means we can tailor our solutions to meet the unique needs of your industry.
            </p>
            <button 
              onClick={scrollToContact}
              className="bg-white text-primary-700 hover:bg-primary-50 px-8 py-3 rounded-lg font-medium transition-colors duration-200 inline-flex items-center space-x-2"
            >
              <span>Let's Discuss Your Needs</span>
              <ArrowRight size={20} />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Industries;
